import React, { useState, useCallback, useEffect } from 'react';
import { Outlet, Link } from 'react-router-dom';
import { Frame, TopBar, Text, Box } from '@shopify/polaris';
import { MenuMajor } from '@shopify/polaris-icons';
import Navigation from './Navigation';
import ScrollToTop from './ScrollToTop';

const AppLayout = () => {
  const [navigationActive, setNavigationActive] = useState(true); // Default to visible
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  // Toggle navigation
  const toggleNavigation = useCallback(() => {
    setNavigationActive((navigationActive) => !navigationActive);
  }, []);

  // Handle screen resize
  useEffect(() => {
    const handleResize = () => {
      const smallScreen = window.innerWidth < 768;
      setIsSmallScreen(smallScreen);
      
      // On small screens, always start with navigation closed
      // On larger screens, keep the current state
      if (smallScreen) {
        setNavigationActive(false);
      }
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Close navigation when clicking outside on small screens
  const handleContentClick = useCallback(() => {
    if (isSmallScreen && navigationActive) {
      setNavigationActive(false);
    }
  }, [isSmallScreen, navigationActive]);

  // Store navigation state in localStorage to persist between sessions
  useEffect(() => {
    if (!isSmallScreen) {
      localStorage.setItem('navigationActive', JSON.stringify(navigationActive));
    }
  }, [navigationActive, isSmallScreen]);

  // Load navigation state from localStorage on initial load
  useEffect(() => {
    const storedNavState = localStorage.getItem('navigationActive');
    if (storedNavState !== null && !isSmallScreen) {
      setNavigationActive(JSON.parse(storedNavState));
    }
  }, [isSmallScreen]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Custom header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        padding: '0.5rem 1rem',
        borderBottom: '1px solid #ddd',
        backgroundColor: '#fff'
      }}>
        {/* Left section with menu toggle and logo */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button 
            onClick={toggleNavigation}
            style={{ 
              background: 'none', 
              border: 'none', 
              cursor: 'pointer',
              padding: '8px',
              marginRight: '8px'
            }}
            aria-label="Toggle menu"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 4H17V6H3V4ZM3 9H17V11H3V9ZM3 14H17V16H3V14Z" fill="currentColor"/>
            </svg>
          </button>
          <img 
            src="/logo.png" 
            alt="Editify" 
            style={{ height: '32px', width: 'auto' }} 
          />
        </div>
        
        {/* Center section with app name */}
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <Text variant="headingLg" as="h1" fontWeight="bold">
              Editify
            </Text>
            <Text variant="bodyMd" as="span" color="subdued">
              - AI-Powered Product Editing for Shopify Sellers
            </Text>
          </div>
        </div>
        
        {/* Right section (empty for now, for balance) */}
        <div style={{ width: '32px' }}></div>
      </div>
      
      {/* Main content area with navigation and outlet */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Mobile navigation overlay */}
        {navigationActive && isSmallScreen && (
          <div 
            style={{ 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              backgroundColor: 'rgba(0,0,0,0.5)', 
              zIndex: 10 
            }} 
            onClick={toggleNavigation}
          />
        )}
        
        {/* Navigation sidebar */}
        <div 
          style={{ 
            width: navigationActive ? '240px' : '0', 
            transition: 'width 0.3s',
            overflow: 'hidden',
            borderRight: '1px solid #ddd',
            backgroundColor: '#fff',
            zIndex: 20,
            position: isSmallScreen ? 'fixed' : 'relative',
            height: isSmallScreen ? '100%' : 'auto',
            boxShadow: isSmallScreen && navigationActive ? '2px 0 10px rgba(0,0,0,0.2)' : 'none'
          }}
        >
          <Navigation />
        </div>
        
        {/* Main content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '0 20px', paddingBottom: '60px' }} onClick={handleContentClick}>
          <Outlet />
          <ScrollToTop />
        </div>
      </div>
    </div>
  );
};

export default AppLayout;
